'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from 'lucide-react'

import { useAlgoliaClients } from '@/shared/hooks/useAlgoliaClients'
import { useDebouncedValue } from '@/shared/lib/use-debounced-value'
import { Input } from '@/shared/ui/input.tsx'
import { Loader } from '@/shared/ui/loader'
import { Pagination } from '@/shared/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select.tsx'

import { ClientCard } from './entities/client/client-card'

const HITS_PER_PAGE = 20

export default function ClientsPage() {
    const { t } = useTranslation()
    const [searchTerm, setSearchTerm] = useState('')
    const [classificationFilter, setClassificationFilter] = useState('all')
    const [page, setPage] = useState(0)

    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)

    useEffect(() => {
        setPage(0)
    }, [debouncedSearchTerm, classificationFilter])

    const { results: clients, isLoading, totalHits, totalPages, currentPage } = useAlgoliaClients({
        searchQuery: debouncedSearchTerm,
        classificationFilter,
        page,
        hitsPerPage: HITS_PER_PAGE,
    })

    const showingFrom = totalHits > 0 ? currentPage * HITS_PER_PAGE + 1 : 0
    const showingTo = Math.min((currentPage + 1) * HITS_PER_PAGE, totalHits)

    return (
        <div className='container mx-auto space-y-6 p-6'>
            <h1 className='text-3xl font-bold'>{t('clients.title', 'Clientes')}</h1>

            <div className='flex flex-wrap items-center justify-between gap-4'>
                <div className='min-w-[300px] flex-1'>
                    <div className='relative'>
                        <Search className='absolute left-2 top-1/2 -translate-y-1/2 transform text-gray-400' />
                        <Input
                            type='text'
                            placeholder={t(
                                'clients.searchPlaceholder',
                                'Buscar por nombre, teléfono, email o nombre de la mascota',
                            )}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className='pl-10'
                        />
                    </div>
                </div>
                <div className='flex gap-4'>
                    <Select value={classificationFilter} onValueChange={setClassificationFilter}>
                        <SelectTrigger className='w-[180px]'>
                            <SelectValue placeholder={t('clients.classification', 'Clasificación')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='all'>{t('clients.classification.all', 'Todas')}</SelectItem>
                            <SelectItem value='normal'>{t('clients.classification.normal', 'Normal')}</SelectItem>
                            <SelectItem value='staff'>{t('clients.classification.staff', 'Trabajador')}</SelectItem>
                            <SelectItem value='VIP'>{t('clients.classification.vip', 'VIP')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {!isLoading && totalHits > 0 && (
                <p className='text-sm text-muted-foreground'>
                    {t('clients.pagination.showing', 'Mostrando {{from}}–{{to}} de {{total}} clientes', {
                        from: showingFrom,
                        to: showingTo,
                        total: totalHits,
                    })}
                </p>
            )}

            {isLoading ? (
                <Loader className='py-12' />
            ) : clients.length === 0 ? (
                <p className='py-12 text-center text-muted-foreground'>
                    {t('clients.empty', 'No se encontraron clientes')}
                </p>
            ) : (
                <div className='space-y-4'>
                    {clients.map(client => (
                        <ClientCard key={client.id} client={client} />
                    ))}
                </div>
            )}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
                disabled={isLoading}
            />
        </div>
    )
}
